import { useEffect, useState } from "react";
import axios from "axios";
import { Crosshair, Wind, Moon, Clock, Navigation, Shield, ChevronDown, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function scoreClass(score, isDarkMode) {
  if (score >= 8) return isDarkMode ? "text-emerald-300" : "text-emerald-700";
  if (score >= 6) return isDarkMode ? "text-lime-300" : "text-lime-700";
  if (score >= 4) return isDarkMode ? "text-amber-300" : "text-amber-700";
  return isDarkMode ? "text-orange-300" : "text-orange-700";
}

export function HuntingWidget({ lat, lon, isDarkMode }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!lat || !lon) return;
    let isMounted = true;
    axios
      .get(`${API}/weather/hunting`, { params: { lat, lon }, timeout: 12000 })
      .then((response) => { if (isMounted) setData(response.data); })
      .catch((error) => { console.error("Hunting widget error:", error.message); });
    return () => { isMounted = false; };
  }, [lat, lon]);

  const panel = isDarkMode ? "bg-white/5 border-white/10" : "bg-white/80 border-gray-200";
  const muted = isDarkMode ? "text-white/55" : "text-gray-600";

  if (!data?.success) {
    return (
      <div data-testid="hunting-widget-loading">
        <div className="flex items-center gap-2 mb-3">
          <Crosshair className={`w-5 h-5 ${isDarkMode ? "text-amber-400" : "text-amber-600"}`} />
          <h3 className={`font-semibold ${isDarkMode ? "text-white/80" : "text-gray-800"}`}>Hunting & Scouting</h3>
        </div>
        <div className="animate-pulse space-y-2">
          <div className={`h-6 rounded w-2/3 ${isDarkMode ? "bg-white/10" : "bg-gray-200"}`} />
          <div className={`h-4 rounded w-1/2 ${isDarkMode ? "bg-white/10" : "bg-gray-200"}`} />
        </div>
      </div>
    );
  }

  const { game_activity: gameActivity = {}, moon = {}, solunar = {}, weather: wx = {}, field_strategy: strategy = {}, comfort = {}, safety = {} } = data;
  const deer = gameActivity.deer || {};
  const turkey = gameActivity.turkey || {};
  const waterfowl = gameActivity.waterfowl || {};

  return (
    <div data-testid="hunting-widget" className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Crosshair className={`w-5 h-5 shrink-0 ${isDarkMode ? "text-amber-300" : "text-amber-700"}`} />
          <div className="min-w-0">
            <h3 className={`font-semibold ${isDarkMode ? "text-white/85" : "text-gray-900"}`}>Hunting & Scouting</h3>
            <p className={`text-xs truncate ${muted}`} data-testid="hunting-current-wind-label">Wind {wx.wind_direction} at {wx.wind_speed_mph} mph</p>
          </div>
        </div>
        <div className="text-right shrink-0" data-testid="hunting-deer-score">
          <p className={`text-3xl font-black leading-none ${scoreClass(deer.score, isDarkMode)}`}>{deer.score}</p>
          <p className={`text-[11px] font-semibold ${muted}`}>Deer</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div className={`rounded-xl border p-3 ${panel}`} data-testid="hunting-turkey-score">
          <p className={`text-[11px] ${muted}`}>Turkey</p>
          <p className={`text-lg font-black ${scoreClass(turkey.score, isDarkMode)}`}>{turkey.score}/10</p>
          <p className={`text-[11px] ${muted}`}>{turkey.rating}</p>
        </div>
        <div className={`rounded-xl border p-3 ${panel}`} data-testid="hunting-waterfowl-score">
          <p className={`text-[11px] ${muted}`}>Waterfowl</p>
          <p className={`text-lg font-black ${scoreClass(waterfowl.score, isDarkMode)}`}>{waterfowl.score}/10</p>
          <p className={`text-[11px] ${muted}`}>{waterfowl.rating}</p>
        </div>
        <div className={`rounded-xl border p-3 ${panel}`} data-testid="hunting-comfort-score">
          <p className={`text-[11px] ${muted}`}>Comfort</p>
          <p className={`text-lg font-black ${scoreClass(comfort.score, isDarkMode)}`}>{comfort.score}/10</p>
          <p className={`text-[11px] ${muted}`}>{comfort.rating}</p>
        </div>
      </div>

      <div className={`rounded-xl border p-3 ${panel}`} data-testid="hunting-stand-strategy">
        <div className="flex items-center gap-1.5 mb-1"><Navigation className="w-4 h-4 text-amber-400" /><span className="text-xs font-semibold">Stand strategy</span></div>
        <p className={`text-sm leading-relaxed ${muted}`}>{strategy.wind_strategy}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="hunting-visible-windows-grid">
        {data.hunt_windows?.slice(0, 3).map((window) => (
          <div key={window.label} className={`rounded-xl border p-3 ${panel}`} data-testid={`hunting-visible-window-${window.label.toLowerCase().replaceAll(" ", "-")}`}>
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="text-xs font-semibold truncate">{window.label}</span>
              <span className={`text-xs font-black ${scoreClass(window.score, isDarkMode)}`}>{window.score}/10</span>
            </div>
            <p className="text-xs font-bold">{window.start} - {window.end}</p>
            <p className={`text-[11px] mt-1 line-clamp-2 ${muted}`}>{window.target}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="hunting-visible-field-grid">
        <div className={`rounded-xl border p-3 ${panel}`} data-testid="hunting-visible-scouting-focus">
          <div className="flex items-center gap-1.5 mb-1"><Wind className="w-4 h-4 text-slate-400" /><span className="text-xs font-semibold">Scouting focus</span></div>
          <p className={`text-xs leading-relaxed line-clamp-3 ${muted}`}>{strategy.scouting_focus?.[0] || "Watch recent tracks, feeding edges, and wind-safe access."}</p>
        </div>
        <div className={`rounded-xl border p-3 ${panel}`} data-testid="hunting-visible-moon-safety">
          <div className="flex items-center gap-1.5 mb-1"><Moon className="w-4 h-4 text-yellow-300" /><span className="text-xs font-semibold">Moon & safety</span></div>
          <p className="text-sm font-bold">{moon.phase || "Moon"} · {moon.illumination ?? "—"}%</p>
          <p className={`text-[11px] line-clamp-2 ${muted}`}>{safety.tree_stand_wind_note || solunar.rating || "Check stand safety and scent drift."}</p>
        </div>
      </div>

      <button
        type="button"
        onClick={(event) => { event.stopPropagation(); setExpanded((value) => !value); }}
        className={`w-full flex items-center justify-center gap-2 rounded-xl border px-3 py-2 text-xs font-bold transition-colors ${isDarkMode ? "border-amber-400/20 bg-amber-400/10 text-amber-200 hover:bg-amber-400/20" : "border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100"}`}
        data-testid="hunting-widget-expand-button"
        aria-expanded={expanded}
      >
        {expanded ? "Hide hunting details" : "Show hunting details"}
        <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>

      {expanded && (
        <div className="space-y-3" data-testid="hunting-widget-expanded-panel">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {data.hunt_windows?.map((window) => (
              <div key={window.label} className={`rounded-xl border p-3 ${panel}`} data-testid={`hunting-window-${window.label.toLowerCase().replaceAll(" ", "-")}`}>
                <div className="flex items-center gap-1.5 mb-1"><Clock className="w-3.5 h-3.5 text-amber-400" /><span className="text-xs font-semibold">{window.label}</span></div>
                <p className="text-xs font-bold">{window.start} - {window.end}</p>
                <p className={`text-[11px] mt-1 ${muted}`}>{window.target}</p>
              </div>
            ))}
          </div>

          <div className={`rounded-xl border p-3 ${panel}`} data-testid="hunting-scouting-focus-panel">
            <div className="flex items-center gap-1.5 mb-2"><Wind className="w-4 h-4 text-slate-400" /><span className="text-xs font-semibold">Scouting focus</span></div>
            {strategy.scouting_focus?.map((item) => <p key={item} className={`text-xs mb-1 ${muted}`}>{item}</p>)}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className={`rounded-xl border p-3 ${panel}`} data-testid="hunting-moon-solunar-panel">
              <div className="flex items-center gap-1.5 mb-1"><Moon className="w-4 h-4 text-yellow-300" /><span className="text-xs font-semibold">Moon & solunar</span></div>
              <p className="text-sm font-bold">{moon.phase} · {moon.illumination}%</p>
              <p className={`text-xs ${muted}`}>{solunar.rating} activity</p>
            </div>
            <div className={`rounded-xl border p-3 ${panel}`} data-testid="hunting-safety-panel">
              <div className="flex items-center gap-1.5 mb-1"><Shield className="w-4 h-4 text-emerald-400" /><span className="text-xs font-semibold">Safety</span></div>
              <p className={`text-xs ${muted}`}>{safety.tree_stand_wind_note}</p>
            </div>
          </div>

          {safety.hazards?.length > 0 && (
            <div className={`rounded-xl border p-3 ${isDarkMode ? "bg-orange-500/10 border-orange-400/20" : "bg-orange-50 border-orange-200"}`} data-testid="hunting-hazards-panel">
              <div className="flex items-center gap-1.5 mb-1"><AlertTriangle className="w-4 h-4 text-orange-400" /><span className="text-xs font-semibold">Hazards</span></div>
              {safety.hazards.map((hazard) => <p key={hazard} className={`text-xs ${muted}`}>{hazard}</p>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}